/**
 * Auth state cleanup utilities — called on ANY auth timeout or failure.
 *
 * Root cause of the "permanent purple spinner" bug:
 *   Supabase caches auth tokens in localStorage/IndexedDB. When a cached
 *   token is stale or the session lock is held by a previous tab, the
 *   onAuthStateChange listener hangs indefinitely. Simply calling
 *   window.location.reload() or showing a recovery UI re-triggers the
 *   same failing code path.
 *
 * Correct fix: on timeout, wipe Supabase local state (localStorage keys +
 * IndexedDB) so the next page load starts completely clean, then
 * hard-navigate to /login (NOT reload). IDB deletions are awaited before
 * the redirect so stale tokens cannot survive into the next page load.
 *
 * Rules:
 *  - clearAndRedirect MUST be called before every redirect to /login
 *    that originates from an auth failure / timeout
 *  - Never call window.location.reload() in any auth recovery path
 *  - All errors are swallowed — this runs in degraded state
 */

// Known IndexedDB database names used by Supabase auth
const SUPABASE_IDB_NAMES = [
  'supabase',
  'sb-auth-token',
  'supabase-auth',
  'keyval-store',        // idb-keyval (used by some Supabase SDK versions)
  'localforage',         // legacy fallback store
]

/**
 * Promisifies a single indexedDB.deleteDatabase call.
 * Always resolves — never rejects — so a blocked or errored deletion
 * cannot prevent the redirect from firing.
 */
function deleteIDB(name: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name)
      req.onsuccess = () => resolve()
      req.onerror   = () => resolve()   // non-critical — proceed anyway
      req.onblocked = () => resolve()   // another tab has it open — proceed anyway
    } catch {
      resolve()
    }
  })
}

/**
 * Wipes Supabase auth state from sessionStorage and localStorage.
 * Synchronous — safe to call from any context.
 * IndexedDB is handled separately in clearAndRedirect (async, awaited).
 */
export function clearAllAuthState(reason: string): void {
  console.log(`[Nudge/auth] Clearing local state — reason: ${reason}`)

  // sessionStorage: quiz caches, Gemini call counts, etc.
  try {
    sessionStorage.clear()
    console.log('[Nudge/auth] sessionStorage cleared')
  } catch {
    // Restricted in some privacy modes — ignore
  }

  // localStorage: remove only Supabase auth keys — never clear() the whole store
  try {
    const projectRef = import.meta.env.VITE_SUPABASE_URL
      .split('//')[1].split('.')[0]
    const supabaseKey = `sb-${projectRef}-auth-token`
    localStorage.removeItem(supabaseKey)
    localStorage.removeItem(`${supabaseKey}.code-verifier`)
    console.log('[Nudge/auth] Supabase localStorage keys removed')
  } catch {
    // Restricted in some privacy modes — ignore
  }
}

/**
 * Hard-navigates to /login with a reason param so the login page can show
 * an appropriate "starting fresh session" message.
 *
 * Uses window.location.replace so the broken page is removed from history —
 * pressing Back won't return to the spinner.
 */
export function redirectToFreshLogin(reason = 'timeout'): void {
  console.log('[Nudge/auth] Redirecting to fresh login')
  window.location.replace(`/login?reason=${encodeURIComponent(reason)}`)
}

/**
 * Wipe all Supabase auth state (sync + IDB) then redirect to login.
 * IDB deletions are awaited in parallel before the redirect fires —
 * stale tokens cannot survive into the next page load.
 * This is the ONLY recovery path for any auth hang or failure.
 */
export async function clearAndRedirect(reason: string): Promise<void> {
  clearAllAuthState(reason)
  await Promise.all(SUPABASE_IDB_NAMES.map(deleteIDB))
  redirectToFreshLogin(reason)
}
